import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'gatsby';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import Image from '../../../components/shared/image';
import { useProductTree } from '../../../graphql/hooks/use-product-tree';
import {
  categoryPathToUrl,
  getChildrenForPath,
  getMenuPathPrefix,
  getNodeByPath,
  productPathToUrl,
  type ProductTreeNode,
} from '../../../lib/product-tree';

type ProductDrilldownProps = {
  onBackToMain: () => void;
  onClose: () => void;
};

function categoryHasNextStep(
  node: ProductTreeNode,
  productsByCategorySlug: Map<string, { slug: string }[]>,
): boolean {
  if (node.children.length > 0) return true;
  return (productsByCategorySlug.get(node.slug) ?? []).length > 0;
}

const ProductDrilldown: FC<ProductDrilldownProps> = ({
  onBackToMain,
  onClose,
}) => {
  const { roots, productsByCategorySlug } = useProductTree();
  const pathPrefix = useMemo(() => getMenuPathPrefix(roots), [roots]);
  const [activePath, setActivePath] = useState<string[]>(pathPrefix);

  useEffect(() => {
    setActivePath(pathPrefix);
  }, [pathPrefix]);

  const activeNode = useMemo(
    () => getNodeByPath(roots, activePath),
    [roots, activePath],
  );

  const childCategories = useMemo(
    () => getChildrenForPath(roots, activePath),
    [roots, activePath],
  );

  const leafProducts = useMemo(() => {
    if (!activeNode || activeNode.children.length > 0) return [];
    return productsByCategorySlug.get(activeNode.slug) ?? [];
  }, [activeNode, productsByCategorySlug]);

  const isAtRootLevel = activePath.length === pathPrefix.length;
  const showProducts = Boolean(activeNode && leafProducts.length > 0);

  const panelTitle = useMemo(() => {
    if (isAtRootLevel) return 'MACHINES';
    return activeNode?.menuLabel ?? activeNode?.title ?? 'Products';
  }, [isAtRootLevel, activeNode]);

  const previewProduct = leafProducts[0];
  const previewImageUrl =
    previewProduct?.headerImageUrl ||
    previewProduct?.thumbnailUrl ||
    activeNode?.previewImageUrl ||
    '';

  const handleBack = () => {
    if (isAtRootLevel) {
      onBackToMain();
      return;
    }
    setActivePath(activePath.slice(0, -1));
  };

  if (!roots.length) {
    return (
      <div className="product-drilldown">
        <Link className="product-drilldown__link" onClick={onClose} to="/products">
          View all products
        </Link>
      </div>
    );
  }

  return (
    <div className="product-drilldown">
      <div className="product-drilldown__toolbar">
        <button
          className="product-drilldown__back"
          onClick={handleBack}
          type="button"
        >
          <ChevronLeft aria-hidden size={20} strokeWidth={2} />
          <span>{isAtRootLevel ? 'Menu' : 'Back'}</span>
        </button>
        <p className="product-drilldown__title">
          <Link
            className="product-drilldown__title-link"
            onClick={onClose}
            to={categoryPathToUrl(activePath)}
          >
            {panelTitle}
          </Link>
        </p>
        <Link
          className="product-drilldown__hub-link"
          onClick={onClose}
          to="/products"
        >
          All
        </Link>
      </div>

      <ul className="product-drilldown__list">
        {childCategories.map((node) => {
          const pathAtItem = [...activePath, node.slug];
          const hasNext = categoryHasNextStep(node, productsByCategorySlug);

          if (hasNext) {
            return (
              <li key={node.slug}>
                <button
                  className="product-drilldown__row"
                  onClick={() => {
                    setActivePath(pathAtItem);
                  }}
                  type="button"
                >
                  <span>{node.menuLabel}</span>
                  <ChevronRight aria-hidden size={18} strokeWidth={2} />
                </button>
              </li>
            );
          }

          return (
            <li key={node.slug}>
              <Link
                className="product-drilldown__row"
                onClick={onClose}
                to={categoryPathToUrl(pathAtItem)}
              >
                <span>{node.menuLabel}</span>
              </Link>
            </li>
          );
        })}

        {showProducts
          ? leafProducts.map((product) => (
              <li key={product.slug}>
                <Link
                  className="product-drilldown__row"
                  onClick={onClose}
                  to={productPathToUrl(product.categoryPath, product.slug)}
                >
                  <span>{product.title}</span>
                </Link>
              </li>
            ))
          : null}
      </ul>

      <AnimatePresence>
        {previewImageUrl ? (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="product-drilldown__preview-wrap"
            exit={{ opacity: 0, y: 8 }}
            initial={{ opacity: 0, y: 8 }}
          >
            <Link
              className="product-drilldown__preview-card"
              onClick={onClose}
              to={
                previewProduct
                  ? productPathToUrl(
                      previewProduct.categoryPath,
                      previewProduct.slug,
                    )
                  : categoryPathToUrl(activePath)
              }
            >
              <div className="product-drilldown__preview-media">
                <Image
                  alt={previewProduct?.title ?? panelTitle}
                  className="product-drilldown__preview-img"
                  height={200}
                  src={previewImageUrl}
                  width={280}
                />
              </div>
              {previewProduct ? (
                <p className="product-drilldown__preview-title">
                  {previewProduct.title}
                </p>
              ) : null}
            </Link>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default ProductDrilldown;
